import { useMemo } from 'react';
import { AlertTriangle, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AttentionLinesPanel,
  ProceedAckFooter,
  Section,
  Grid,
  Row,
  classifyPostingLines,
  postingLineLabel,
} from '@shared/posting';
import {
  PICKUP_TYPE_OPTIONS,
  WAIVED_FREIGHT_OPTIONS,
  type QuotationDraft,
} from '../../types/quotation.types';
import { isEditableRow } from '../../utils/lineItem';
import { computePricing, computePricingContext, markupForGrade } from '../../utils/pricing';
import type { BaseMarkup } from '@features/quotation-intake/types/quotationIntake.types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: QuotationDraft;
  /** Panel-mount base markup — drives the per-line no-markup price gate (86d3j5qca). */
  baseMarkup: BaseMarkup | null;
  /** Freight cabinet touched but no computed total → posts without a freight line (already
   *  suppressed for pick-up / freight-incorporated / waived). */
  freightPartial: boolean;
  onConfirm: () => void;
  isSubmitting: boolean;
}

// Cutting List posting review (86d3hm2vr) — replaces the two native window.confirm gates.
// A wide, read-only summary of what will post to NetSuite as an Estimate, the lines needing
// attention, and a two-step confirm. Mirrors the Sales Order PreSubmitReviewDialog.
export function CuttingListPostDialog({
  open,
  onOpenChange,
  draft,
  baseMarkup,
  freightPartial,
  onConfirm,
  isSubmitting,
}: Props) {
  const classified = useMemo(() => {
    const adjustment = computePricingContext(draft.lineItems)?.adjustment ?? 0;
    return classifyPostingLines(
      draft.lineItems
        .filter((li) => isEditableRow(li))
        .map((li) => ({
          key: li.id,
          label: postingLineLabel(li),
          diameterMm: li.diameterMm,
          gradeCode: li.gradeCode,
          isWeldable: li.isWeldable,
          matchedItemId: li.matchedItemId ?? null,
          quantity: li.quantity,
          // 86d3j5qca — gate CL lines on a resolvable, non-zero markup (matches the SO price
          // gate). computePricing is null when there's no rate at all; the extra base-markup > 0
          // check also catches a zero/empty base (which computePricing treats as a valid rate)
          // and unmapped grades like G33 — blocking a post-at-cost line here, before the
          // irreversible confirm, instead of as a server rejection afterward.
          hasResolvablePrice:
            computePricing(li, baseMarkup, adjustment) != null &&
            (markupForGrade(li.gradeCode, baseMarkup) ?? 0) > 0,
          // NOTE: no kg/UoM handling here. `kgs` is vestigial (extraction defaults to pcs)
          // and a weight-only RFQ is really a Firm Offer — but that determination is
          // nuanced (vs an extraction miss) and belongs to a separate FO-detection ticket.
        })),
    );
  }, [draft.lineItems, baseMarkup]);

  const pickupLabel = PICKUP_TYPE_OPTIONS.find((o) => o.value === draft.pickup)?.label ?? '—';
  const waivedLabel =
    WAIVED_FREIGHT_OPTIONS.find((o) => o.value === draft.waivedFreight)?.label ?? '—';
  const customer = draft.customerName?.trim() || '(no customer)';

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Cutting List — review</DialogTitle>
          <DialogDescription>
            This posts the quotation to NetSuite as an Estimate. The draft becomes read-only and
            a permanent QD- number is assigned. This can't be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Section title="Quotation">
            <Grid>
              <Row label="Customer" value={customer} className="col-span-2" />
              <Row label="Quotation #" value={draft.quotationNumber} mono />
              <Row label="Pick-up" value={pickupLabel} />
              <Row label="Waived freight" value={waivedLabel} />
            </Grid>
          </Section>

          {freightPartial && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2.5 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <span>
                Freight is partially filled (destination, truck type, or total is missing) — this
                Cutting List will post <span className="font-medium">without a freight line</span>.
              </span>
            </div>
          )}

          <Section title="Line items">
            <AttentionLinesPanel lines={classified} />
            {classified.blocking.length === 0 &&
              classified.excluded.length === 0 &&
              classified.attention.length === 0 && (
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" /> All line items are ready to post.
                </p>
              )}
          </Section>
        </div>

        <ProceedAckFooter
          blockingCount={classified.blocking.length}
          attentionCount={classified.attention.length}
          isSubmitting={isSubmitting}
          onCancel={() => onOpenChange(false)}
          onConfirm={onConfirm}
          confirmLabel="Create Cutting List"
          confirmIcon={<FileText className="mr-1.5 h-4 w-4" />}
        />
      </DialogContent>
    </Dialog>
  );
}
